import { Box, Grid, makeStyles } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useQuery } from "@tanstack/react-query";
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import GenericButton from "components/Buttons/GenericButton";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import { listContractApproval } from "crud/contractApproval.crud";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import FilterSection from "./FilterSection";
import ResultSection from "./ResultSection";

import LocationHeader from "components/Headers/LocationHeader";
import useFilterSync from "hooks/useFilterSync";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { getReportAction } from "redux/actions/reportsAction";
import { CONTRACT_APPROVAL, CONTRACT_APPROVAL_WORKCENTER_EMPLOYEE } from "utils/actionsConsts";
import { PAGINATION, REPORT_CONSTS, REPORT_FILE_NAME, REPORT_TYPES } from "utils/const";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const useStylesLocal = makeStyles((theme) => ({
  headerRow: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    marginBottom: "1rem",
    columnGap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
      rowGap: theme.spacing(1),
    },
  },
  createButton: {
    marginBottom: "37px",
    [theme.breakpoints.down("xs")]: {
      marginBottom: 0,
      width: "100%",
    },
  },
}));

function ContractApprovalListInner({ defaultFormValues, goToContractApprovalCreate }) {
  const classes = useStylesGeneric();
  const localClasses = useStylesLocal();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("xs"));
  const { watch, getValues, setValue } = useFormContext();
  const location = useLocation();
  const dispatch = useDispatch();

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const submittedFilterRef = useRef(null);
  const pageSize = watch("pageSize") ?? defaultFormValues.pageSize;

  const buildPayload = (values, override) => {
    const effectivePageIndex = override?.pageIndex ?? values.pageIndex ?? 1;
    const effectivePageSize = override?.pageSize ?? values.pageSize ?? defaultFormValues.pageSize;
    const effectiveSortColumn = override?.sortColumn ?? sortColumn;
    const effectiveSortDirection = override?.sortDirection ?? sortDirection;

    return {
      requestNumber: values.requestNumber ?? "",
      workerName: values.workerName ?? "",
      ssn: values.ssn ?? "",
      nif: values.nif ?? "",
      contractTypeId: values.contractTypeId ?? "",
      contractApprovalStatusId: values.contractApprovalStatusId ?? "",
      startDate: values.startDate ? values.startDate?.format() : null,
      endDate: values.endDate ? values.endDate?.format() : null,
      pageIndex: effectivePageIndex,
      pageSize: effectivePageSize,
      sortColumn: effectiveSortColumn,
      sortDirection: effectiveSortDirection,
    };
  };

  const handleExport = (extension) => {
    const now = new Date();
    const y = now.getFullYear();
    const mo = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const h = String(now.getHours()).padStart(2, "0");
    const mi = String(now.getMinutes()).padStart(2, "0");
    const timestamp = `${y}${mo}${d}${h}${mi}`;

    const base = submittedFilterRef.current ?? buildPayload(getValues());
    const filterToExport = {
      ...base,
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: REPORT_CONSTS.MAX_RESULTS,
    };

    const reportPayload = {
      reportEnumKey: REPORT_TYPES.CONTRACT_APPROVALS_SEARCH_RESULTS,
      parameters: JSON.stringify(filterToExport),
    };

    const fileName = `${REPORT_FILE_NAME.CONTRACT_APPROVALS_SEARCH_RESULTS}${timestamp}${extension}`;
    dispatch(getReportAction(reportPayload, fileName));
  };

  // Se a URL já tem query params, evita disparar a query com defaults.
  // O useFilterSync vai aplicar a URL no form e então (via onFilterChange) setar queryParams.
  const hasInitialSearchParams = (location.search || "") !== "";
  const [queryParams, setQueryParams] = useState(() => (hasInitialSearchParams ? null : buildPayload(getValues())));

  const contractApprovalQuery = useQuery({
    queryKey: ["listContractApproval", queryParams],
    queryFn: () => listContractApproval(queryParams),
    enabled: !!queryParams,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!submittedFilterRef.current && queryParams) {
      submittedFilterRef.current = queryParams;
    }
  }, [queryParams]);

  const filterSync = useFilterSync({
    defaultValues: defaultFormValues,
    syncPagination: true,
    pagination: {
      pageIndexKey: "pageIndex",
      pageSizeKey: "pageSize",
      defaultPageSize: defaultFormValues.pageSize,
    },
    onFilterChange: (values) => {
      const next = buildPayload(values || getValues());

      submittedFilterRef.current = next;
      setQueryParams(next);
    },
  });

  return (
    <Grid container classes={{ root: classes.containerRoot }}>
      <LocationHeader section="Homologação de contratos" />
      <Grid item xs={12} md={12} className={localClasses.headerRow}>
        <PageTitleHeader title="Pedidos de Homologação de contratos" small={isXs} />
        <GenericButton
          typeSubmit={false}
          size="medium"
          color="tertiary"
          onClick={() => goToContractApprovalCreate()}
          fullWidth={isXs}
          className={localClasses.createButton}
        >
          Criar Pedido de Homologação de Contrato
        </GenericButton>
      </Grid>

      <Grid item xs={12} md={12}>
        <FilterSection
          exportDisabled={contractApprovalQuery.isFetching}
          onExport={handleExport}
          onSubmit={() => {
            const values = getValues();
            const nextFormValues = {
              ...values,
              pageIndex: 1,
              pageSize: values.pageSize ?? defaultFormValues.pageSize,
            };

            if ((values.pageIndex ?? 1) !== 1) setValue("pageIndex", 1);

            filterSync.handleFilterSubmit(nextFormValues);

            const next = buildPayload(nextFormValues, {
              pageIndex: 1,
              pageSize: nextFormValues.pageSize,
            });

            submittedFilterRef.current = next;
            setQueryParams(next);
          }}
          onReset={() => {
            filterSync.handleFilterReset();

            const next = buildPayload(defaultFormValues, {
              pageIndex: 1,
              pageSize: defaultFormValues.pageSize,
            });

            submittedFilterRef.current = next;
            setQueryParams(next);
          }}
        />
      </Grid>

      <Grid item xs={12} md={12}>
        <Box classes={{ root: classes.tableResultBoxRoot }}>
          <ResultSection
            data={contractApprovalQuery.data}
            isLoading={contractApprovalQuery.isFetching}
            sortColumn={sortColumn}
            setSortColumn={setSortColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            onSortChange={(nextSortColumn, nextSortDirection) => {
              filterSync.updatePagination(1, pageSize);

              const base = submittedFilterRef.current ?? buildPayload(getValues());
              const next = {
                ...base,
                pageIndex: 1,
                pageSize,
                sortColumn: nextSortColumn,
                sortDirection: nextSortDirection,
              };

              submittedFilterRef.current = next;
              setQueryParams(next);
            }}
            onPageChange={(pageIndex) => {
              filterSync.updatePagination(pageIndex, pageSize);

              const base = submittedFilterRef.current ?? buildPayload(getValues());
              const next = {
                ...base,
                pageIndex,
                pageSize,
              };

              submittedFilterRef.current = next;
              setQueryParams(next);
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
}

const ContractApprovalList = (props) => {
  const dispatch = useDispatch();

  const defaultFormValues = {
    requestNumber: "",
    startDate: null,
    endDate: null,
    workerName: "",
    ssn: "",
    nif: "",
    contractTypeId: "",
    contractApprovalStatusId: "",
    pageIndex: 1,
    pageSize: PAGINATION.PAGE_SIZE,
  };

  const form = useForm({
    mode: "onChange",
    defaultValues: defaultFormValues,
  });

  const goToContractApprovalCreate = () => {
    dispatch({
      type: CONTRACT_APPROVAL.CLEAR_CONTRACT_APPROVAL_REQUEST,
    });
    dispatch({
      type: CONTRACT_APPROVAL_WORKCENTER_EMPLOYEE.CLEAR_CONTRACT_APPROVAL_WORKCENTER_EMPLOYEE_REQUEST,
    });
    props.history.push("/admin/contract-approval/create");
  };

  return (
    <FormProvider {...form}>
      <ContractApprovalListInner
        defaultFormValues={defaultFormValues}
        goToContractApprovalCreate={goToContractApprovalCreate}
      />
    </FormProvider>
  );
};

export default ContractApprovalList;
